/* ==========================================================================
   T3 — infinite scroll for the collection grid.

   Products keep arriving as you reach the end of the list instead of the
   visitor having to find a numbered pager. Notes:

   - the numbered pagination stays in the DOM and is only hidden once this
     script has taken over, so the page still works with JS off and crawlers
     keep following the per-page links;
   - pages are fetched through Shopify's section-rendering API — the same route
     facets.js uses — so the active filters and sort order travel along in the
     query string without this file having to know anything about them;
   - facets.js replaces #ProductGridContainer's innerHTML on every filter or
     sort change, which throws our sentinel away with it. A MutationObserver on
     that container re-arms. It watches direct children only, so appending cards
     inside #product-grid cannot retrigger it.

   Everything lives inside the guard block on purpose: Shopify's minifier
   unwraps a top-level IIFE, so that is not a safe wrapper, but declarations
   inside a block stay block-scoped even if the file is served twice.
   ========================================================================== */

if (!window.__t3InfiniteLoaded) {
  window.__t3InfiniteLoaded = true;

  // Start fetching well before the sentinel is actually on screen, so the next
  // rows are usually already in place by the time the reader gets to them.
  const T3_ROOT_MARGIN = '800px 0px';

  const t3i = {
    sentinel: null,
    observer: null,
    mutations: null,
    loading: false,
    lastFetched: null,
  };

  const t3Container = () => document.getElementById('ProductGridContainer');
  const t3Grid = () => document.getElementById('product-grid');

  function t3NextLink() {
    const container = t3Container();
    return container ? container.querySelector('.pagination-wrapper a[rel="next"]') : null;
  }

  function t3Announce(message) {
    const status = t3i.sentinel && t3i.sentinel.querySelector('.t3-infinite__status');
    if (status) status.textContent = message;
  }

  function t3ShowMoreButton(show) {
    const button = t3i.sentinel && t3i.sentinel.querySelector('.t3-infinite__more');
    if (button) button.hidden = !show;
  }

  function t3SetBusy(busy) {
    t3i.loading = busy;
    const spinner = t3i.sentinel && t3i.sentinel.querySelector('.t3-infinite__spinner');
    if (spinner) spinner.hidden = !busy;
  }

  function t3BuildSentinel() {
    const el = document.createElement('div');
    el.className = 't3-infinite';
    el.innerHTML =
      '<div class="t3-infinite__spinner" hidden aria-hidden="true"></div>' +
      '<button type="button" class="t3-infinite__more" hidden>Load more</button>' +
      '<p class="t3-infinite__status" role="status" aria-live="polite"></p>';
    el.querySelector('.t3-infinite__more').addEventListener('click', () => t3LoadNext());
    return el;
  }

  function t3LoadNext() {
    if (t3i.loading) return;

    const link = t3NextLink();
    const grid = t3Grid();
    if (!link || !grid || !grid.dataset.id) return;

    const url = new URL(link.href, window.location.href);
    url.searchParams.set('section_id', grid.dataset.id);
    const href = url.toString();

    // If the response ever comes back pointing at the page we just asked for,
    // stop rather than fetch it forever.
    if (href === t3i.lastFetched) {
      t3Stop();
      return;
    }
    t3i.lastFetched = href;

    t3SetBusy(true);
    t3ShowMoreButton(false);

    fetch(href)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const incoming = doc.getElementById('product-grid');
        const current = t3Grid();
        if (!incoming || !current) throw new Error('product grid missing from response');

        const added = incoming.children.length;
        while (incoming.firstElementChild) {
          const item = incoming.firstElementChild;
          // The reveal-on-scroll animation starts a card hidden and is armed at
          // page load; a card appended afterwards is never observed and would
          // stay invisible, so appended cards opt out of it.
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.removeAttribute('data-cascade');
          item.removeAttribute('style');
          current.appendChild(item);
        }

        // Swap in the fetched pager so the next href moves on with us.
        const pager = t3Container().querySelector('.pagination-wrapper');
        const nextPager = doc.querySelector('.pagination-wrapper');
        if (pager) {
          if (nextPager) pager.replaceWith(nextPager);
          else pager.remove();
        }

        t3SetBusy(false);
        t3Announce(`${added} more products loaded.`);
        if (!t3NextLink()) t3Stop();
      })
      .catch(() => {
        t3SetBusy(false);
        // Never leave the reader at a dead end — hand them the button instead.
        t3i.lastFetched = null;
        t3ShowMoreButton(true);
        t3Announce('Could not load more products.');
      });
  }

  function t3Stop() {
    if (t3i.observer) t3i.observer.disconnect();
    t3i.observer = null;
    t3ShowMoreButton(false);
    // Collapse the sentinel once there is nothing left to fetch, so it cannot
    // leave a band of empty space sitting above the footer.
    if (t3i.sentinel) t3i.sentinel.hidden = true;
  }

  function t3Init() {
    if (t3i.observer) t3i.observer.disconnect();
    t3i.observer = null;
    t3i.sentinel = null;
    t3i.loading = false;
    t3i.lastFetched = null;

    const container = t3Container();
    const grid = t3Grid();
    if (!container || !grid) return;

    // Nothing to take over when everything already fits on one page.
    const hasNext = !!t3NextLink();
    container.classList.toggle('t3-infinite-on', hasNext);
    if (!hasNext) return;

    const host = grid.closest('.collection') || container;
    const sentinel = t3BuildSentinel();
    host.appendChild(sentinel);
    t3i.sentinel = sentinel;

    if (!('IntersectionObserver' in window)) {
      t3ShowMoreButton(true);
      return;
    }

    t3i.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) t3LoadNext();
      },
      { rootMargin: T3_ROOT_MARGIN }
    );
    t3i.observer.observe(sentinel);
  }

  function t3Boot() {
    if (!t3Container()) return;
    t3Init();

    if (t3i.mutations) t3i.mutations.disconnect();
    t3i.mutations = new MutationObserver(() => {
      window.requestAnimationFrame(t3Init);
    });
    t3i.mutations.observe(t3Container(), { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', t3Boot);
  } else {
    t3Boot();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', t3Boot);
}
